import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

interface MentorSuggestion {
  id: string;
  userId: string;
  bio: string | null;
  skillsCanTeach: string[];
  yearsExperience: number | null;
  matchingSkills: string[];
}

/**
 * Suggests mentors for a goal based on skill keyword overlap.
 * Client-side filtering — queries available mentors, then scores
 * them against the goal title and e_types.
 */
export function useGoalMentorSuggestions(
  goalTitle: string,
  eTypes: string[],
  limit = 3,
) {
  const { user } = useAuth();

  const { data: suggestedMentors = [], isLoading } = useQuery<MentorSuggestion[]>({
    queryKey: ['goal-mentor-suggestions', user?.id, goalTitle],
    queryFn: async () => {
      if (!user) return [];

      // Fetch available mentors (not the current user)
      const { data: mentors } = await supabase
        .from('mentor_profiles')
        .select('id, user_id, bio, skills_can_teach, years_experience')
        .eq('is_available_as_mentor', true)
        .neq('user_id', user.id);

      if (!mentors || mentors.length === 0) return [];

      // Build search terms from goal title
      const searchTerms = goalTitle
        .toLowerCase()
        .split(/\s+/)
        .filter(word => word.length > 3); // skip short words

      // Score each mentor by skill overlap
      const scored = mentors
        .map(mentor => {
          const skills = (mentor.skills_can_teach as string[]) ?? [];
          const matchingSkills = skills.filter(skill => {
            const skillLower = skill.toLowerCase();
            return searchTerms.some(term => skillLower.includes(term) || term.includes(skillLower));
          });

          return {
            id: mentor.id,
            userId: mentor.user_id,
            bio: mentor.bio,
            skillsCanTeach: skills,
            yearsExperience: mentor.years_experience,
            matchingSkills,
            score: matchingSkills.length,
          };
        })
        .filter(m => m.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, limit);

      return scored;
    },
    enabled: !!user && goalTitle.length > 0,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  return { suggestedMentors, isLoading };
}
